import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Settings } from 'lucide-react';
import { useProfile } from './ProfileContext';

interface ProfileSwitcherProps {
  compact?: boolean;
}

const ProfileSwitcher: React.FC<ProfileSwitcherProps> = ({ compact = false }) => {
  const { profiles, activeProfile, switchProfile } = useProfile();
  const [isOpen, setIsOpen] = useState(false);

  const handleSwitchProfile = (profileId: string) => {
    switchProfile(profileId);
    setIsOpen(false);
  };

  if (!activeProfile) return null;

  const renderAvatar = (photoUrl: string | undefined | null, avatar: string | undefined, size: 'sm' | 'md') => {
    const sizeClass = size === 'sm' ? 'w-7 h-7' : 'w-8 h-8';
    if (photoUrl) {
      return (
        <img
          src={photoUrl}
          alt="Profile"
          className={`${sizeClass} rounded-full object-cover`}
        />
      );
    }
    return <span className={size === 'sm' ? 'text-lg' : 'text-xl'}>{avatar || '👤'}</span>;
  };

  return (
    <div className="relative">
      {/* Active Profile Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={compact
          ? "flex items-center justify-center w-9 h-9 rounded-full bg-sage-600 hover:bg-sage-500 transition-colors"
          : "flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-sage-200 hover:border-sage-300 transition-colors"
        }
      >
        {renderAvatar((activeProfile as any).photoUrl, activeProfile.avatar, compact ? 'sm' : 'md')}
        {!compact && (
          <>
            <span className="text-sm font-medium text-sage-800">{activeProfile.name}</span>
            <svg
              className={`w-4 h-4 text-sage-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </>
        )}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className={`absolute ${compact ? 'right-0' : ''} top-full mt-2 w-48 bg-white rounded-lg shadow-lg border border-sage-200 z-50`}>
          <div className="py-2">
            {profiles.map((profile) => (
              <button
                key={profile.id}
                onClick={() => handleSwitchProfile(profile.id)}
                className={`w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-sage-50 transition-colors ${profile.isActive ? 'bg-sage-100 text-sage-900' : 'text-sage-700'
                  }`}
              >
                {renderAvatar((profile as any).photoUrl, profile.avatar, 'md')}
                <div>
                  <div className="text-sm font-medium">{profile.name}</div>
                  {profile.ageRange && (
                    <div className="text-xs text-sage-500">{profile.ageRange}</div>
                  )}
                </div>
                {profile.isActive && (
                  <div className="ml-auto w-2 h-2 bg-sage-600 rounded-full"></div>
                )}
              </button>
            ))}
          </div>

          {/* Divider + My Account link */}
          <div className="border-t border-sage-200">
            <NavLink
              to="/profile-settings"
              onClick={() => setIsOpen(false)}
              className="flex items-center gap-2 px-4 py-2.5 text-sm text-sage-600 hover:bg-sage-50 hover:text-sage-800 transition-colors"
            >
              <Settings className="w-4 h-4" />
              <span>My Account</span>
            </NavLink>
          </div>
        </div>
      )}

      {/* Overlay to close dropdown */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default ProfileSwitcher;
